import { motion } from 'framer-motion'
import { AlertTriangle, Zap, Settings } from 'lucide-react'

const problems = [
    {
        icon: AlertTriangle,
        title: 'Agent Deployment Barrier',
        description:
            'Complex Agent workflows (multi-step reasoning, tool-use) lack unified MLOps governance and monitoring, making stable enterprise-scale operation challenging.',
        color: 'from-red-500 to-orange-500',
    },
    {
        icon: Zap,
        title: 'RAG Performance Bottleneck',
        description:
            'Retrieval-Augmented Generation (RAG) systems face high latency and prohibitive costs when querying massive corporate knowledge bases due to slow retrieval and poor context management.',
        color: 'from-yellow-500 to-amber-500',
    },
    {
        icon: Settings,
        title: 'Optimization Gap',
        description:
            'Enterprises lack expertise and tools for efficient fine-tuning using LoRA/QLoRA techniques and fail to fully leverage GPU hardware (CUDA/TensorRT) for inference acceleration.',
        color: 'from-blue-500 to-cyan-500',
    },
]

export const Problems = () => {
    return (
        <section className="py-24 bg-slate-900/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        Market Pain Points
                    </h2>
                    <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                        Core challenges enterprises face when implementing AI at scale
                    </p>
                </motion.div>

                {/* Problems Grid */}
                <div className="grid md:grid-cols-3 gap-8">
                    {problems.map((problem, index) => {
                        const Icon = problem.icon
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ y: -8, scale: 1.02 }}
                                className="group relative"
                            >
                                <div className="h-full p-8 rounded-2xl bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-all duration-300 backdrop-blur-sm">
                                    {/* Gradient Overlay */}
                                    <div
                                        className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${problem.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
                                    />

                                    {/* Icon */}
                                    <div
                                        className={`w-14 h-14 rounded-xl bg-gradient-to-br ${problem.color} flex items-center justify-center mb-6 shadow-lg`}
                                    >
                                        <Icon className="w-7 h-7 text-white" />
                                    </div>

                                    {/* Content */}
                                    <h3 className="text-2xl font-bold mb-4 text-white">
                                        {problem.title}
                                    </h3>
                                    <p className="text-slate-400 leading-relaxed">
                                        {problem.description}
                                    </p>
                                </div>
                            </motion.div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

